import type { ObjectParsedDataByType } from '@server/parser';
import {
  RECORD_TYPE,
  FIELD,
  VALIDATION_RULE,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  PROCESS_BUILDER_FLOW,
  FLOW,
  APEX_TRIGGER,
  APEX_CLASS,
  LAYOUT,
} from '@server/parser';
import type { BaseFieldProperties } from '@server/documentation';
import {
  SWEEP_AUTOMATION,
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
} from '../../components/parser/ConfigurationItemTypes';

/** Minimal shape needed per sweep element — compatible with Automation and RollupDto */
export type SweepItemForMap = {
  id?: string;
  automationId?: string;
  rollupId?: string;
  isActive?: boolean;
};
export type SweepElementsForMap = Partial<Record<string, SweepItemForMap[]>>;

export interface DocumentationItemsMap {
  [RECORD_TYPE]: string[];
  [FIELD]: string[];
  [VALIDATION_RULE]: string[];
  [APPROVAL_PROCESS]: string[];
  [WORKFLOW_RULE]: string[];
  [PROCESS_BUILDER_FLOW]: string[];
  [FLOW]: string[];
  [APEX_TRIGGER]: string[];
  [APEX_CLASS]: string[];
  [LAYOUT]: string[];
  [SWEEP_AUTOMATION]: string[];
  [SWEEP_ALERT]: string[];
  [SWEEP_ASSIGNMENT]: string[];
  [SWEEP_SCHEDULED_ASSIGNMENT]: string[];
  [SWEEP_DEDUPE]: string[];
  [SWEEP_MATCHING]: string[];
}

interface Identifiable {
  metadataId?: string;
  isActive?: boolean;
}

// Configuration types that are part of parsedObjects
const DOCUMENTATION_CONFIGURATION_MAPPING = {
  RECORD_TYPE,
  FIELD,
  VALIDATION_RULE,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  PROCESS_BUILDER_FLOW,
  FLOW,
  APEX_TRIGGER,
  APEX_CLASS,
  LAYOUT,
} as const;

// Maps SweepElementsByType property names to DocumentationItemsMap keys
const SWEEP_ELEMENTS_KEY_MAPPING: Partial<Record<string, keyof DocumentationItemsMap>> = {
  automations: SWEEP_AUTOMATION,
  alerts: SWEEP_ALERT,
  assignments: SWEEP_ASSIGNMENT,
  scheduledAssignments: SWEEP_SCHEDULED_ASSIGNMENT,
  dedup: SWEEP_DEDUPE,
  matching: SWEEP_MATCHING,
};

/**
 * Extracts IDs from an array of objects that have an optional id property and are not explicitly inactive
 */
const extractIds = <T extends Identifiable>(items: T[] | undefined): string[] => {
  if (!items?.length) return [];
  return items
    .filter((item) => item.metadataId && item.isActive !== false)
    .map((item) => item.metadataId!);
};

const getSweepItemId = (item: SweepItemForMap): string | undefined =>
  item.id ?? item.automationId ?? item.rollupId;

const extractSweepElementIds = (items: SweepItemForMap[] | undefined): string[] => {
  if (!items?.length) return [];
  return items
    .filter((item) => getSweepItemId(item) && item.isActive !== false)
    .map((item) => getSweepItemId(item)!);
};

/**
 * Extracts IDs from recordTypes using object.name and recordType.name
 */
const extractRecordTypeIds = (objectData: any): string[] => {
  if (!objectData.recordTypes?.length) return [];
  return objectData.recordTypes
    .filter((recordType: any) => recordType.metadataId && recordType.isActive !== false)
    .map((recordType: any) => recordType.metadataId as string);
};

/**
 * Processes a single object data item and extracts IDs by configuration type
 */
const processDocumentationObjectData = (
  objectData: ObjectParsedDataByType,
): Partial<DocumentationItemsMap> => {
  const result: Partial<DocumentationItemsMap> = {};

  // Special handling for recordTypes
  const recordTypeIds = extractRecordTypeIds(objectData);
  if (recordTypeIds.length > 0) {
    result[RECORD_TYPE] = recordTypeIds;
  }

  // Process other configuration types from documentation objects (excluding recordTypes)
  Object.entries(DOCUMENTATION_CONFIGURATION_MAPPING).forEach(([, targetKey]) => {
    if (targetKey === RECORD_TYPE) return; // Skip recordTypes as it's handled above

    const data = Array.isArray(objectData.objectElements[targetKey])
      ? objectData.objectElements[targetKey]
      : [];

    const ids = extractIds(data as BaseFieldProperties[]);
    if (ids.length > 0) {
      result[targetKey as keyof DocumentationItemsMap] = ids;
    }
  });

  return result;
};

const processSweepElements = (sweepElements: SweepElementsForMap): Partial<DocumentationItemsMap> => {
  const result: Partial<DocumentationItemsMap> = {};

  Object.entries(SWEEP_ELEMENTS_KEY_MAPPING).forEach(([sourceKey, targetKey]) => {
    if (!targetKey) return;
    const ids = extractSweepElementIds(sweepElements[sourceKey]);
    if (ids.length > 0) {
      result[targetKey] = ids;
    }
  });

  return result;
};

/**
 * Merges partial results into the main items map
 */
const mergeResults = (
  target: DocumentationItemsMap,
  source: Partial<DocumentationItemsMap>,
): void => {
  Object.entries(source).forEach(([key, ids]) => {
    if (ids && Array.isArray(ids)) {
      (target as any)[key]?.push(...ids);
    }
  });
};

/**
 * Creates an empty items map with all required properties
 * It doesn't include rollups as they are in fields category
 */
const createEmptyItemsMap = (): DocumentationItemsMap => ({
  [RECORD_TYPE]: [],
  [FIELD]: [],
  [VALIDATION_RULE]: [],
  [APPROVAL_PROCESS]: [],
  [WORKFLOW_RULE]: [],
  [PROCESS_BUILDER_FLOW]: [],
  [FLOW]: [],
  [APEX_TRIGGER]: [],
  [APEX_CLASS]: [],
  [LAYOUT]: [],
  [SWEEP_AUTOMATION]: [],
  [SWEEP_ALERT]: [],
  [SWEEP_ASSIGNMENT]: [],
  [SWEEP_SCHEDULED_ASSIGNMENT]: [],
  [SWEEP_DEDUPE]: [],
  [SWEEP_MATCHING]: [],
});

/**
 * Creates a flat map of item IDs organized by configuration type.
 *
 * @param sweepElements - Sweep elements keyed by AutomationType string value
 * @param parsedObjects - Parsed Salesforce objects from the documentation endpoint
 * @returns DocumentationItemsMap with arrays of IDs for each configuration type
 */
export const createDocumentationItemsMap = (
  sweepElements: SweepElementsForMap = {},
  parsedObjects: ObjectParsedDataByType[] = [],
): DocumentationItemsMap => {
  const itemsMap = createEmptyItemsMap();

  mergeResults(itemsMap, processSweepElements(sweepElements));

  // Process documentation array for standard configuration types
  parsedObjects.forEach((objectData) => {
    const documentationResults = processDocumentationObjectData(objectData);
    mergeResults(itemsMap, documentationResults);
  });

  return itemsMap;
};
